<script lang="ts">
  import { onMount } from 'svelte';
  import { editor } from './lib/store/editor.svelte';
  import { theme } from './lib/store/theme.svelte';
  import { attachPasteHandler, imageFromDropEvent } from './lib/utils/clipboard';
  import { blobToDataURL, decodeImage } from './lib/utils/image';
  import LayersPanel from './lib/components/LayersPanel.svelte';
  import SettingsPanel from './lib/components/SettingsPanel.svelte';
  import Toolbar from './lib/components/Toolbar.svelte';
  import Icon from './lib/components/Icon.svelte';
  import Canvas from './lib/components/canvas/Canvas.svelte';
  import { exportImage } from './lib/rendering/export';
  import type { ToolId } from './lib/domain/types';

  let fileInput = $state<HTMLInputElement>();
  let dragging = $state(false);
  let canvasContainer = $state<HTMLElement>();

  async function handleFile(file: File) {
    if (!file.type.startsWith('image/')) {
      alert('Поддерживаются только изображения.');
      return;
    }
    try {
      const url = await blobToDataURL(file);
      const image = await decodeImage(url);
      editor.setImage(image);
      requestAnimationFrame(() => fitIfReady());
    } catch (err) {
      console.error(err);
      alert('Не удалось открыть изображение.');
    }
  }

  function fitIfReady() {
    if (canvasContainer && editor.project.image) {
      const r = canvasContainer.getBoundingClientRect();
      editor.zoomToFit(r.width, r.height);
    }
  }

  function onFileInputChange(e: Event) {
    const input = e.currentTarget as HTMLInputElement;
    const f = input.files?.[0];
    if (f) handleFile(f);
    input.value = '';
  }

  function onDrop(e: DragEvent) {
    e.preventDefault();
    dragging = false;
    const f = imageFromDropEvent(e);
    if (f) handleFile(f);
  }

  function onDragOver(e: DragEvent) {
    e.preventDefault();
    dragging = true;
  }
  function onDragLeave(e: DragEvent) {
    e.preventDefault();
    if (e.currentTarget === e.target) dragging = false;
  }

  function zoom(delta: number) {
    const vp = editor.project.viewport;
    let newScale = vp.scale + delta;
    newScale = Math.min(8, Math.max(0.1, Math.round(newScale * 10) / 10));
    editor.setViewport(newScale, vp.tx, vp.ty);
  }
  const scalePct = $derived(Math.round(editor.project.viewport.scale * 100));

  async function handleExport() {
    if (!editor.project.image) return;
    try {
      await exportImage(editor.project, 'png');
    } catch (err) {
      console.error(err);
      alert('Не удалось экспортировать изображение');
    }
  }


  const HOTKEYS: Record<string, ToolId> = {
    v: 'select', a: 'arrow', r: 'rect',
    p: 'pencil', t: 'text', n: 'number', b: 'blur',
  };

  function isTypingTarget(t: EventTarget | null): boolean {
    const el = t as HTMLElement | null;
    if (!el) return false;
    return el.isContentEditable || ['INPUT', 'TEXTAREA', 'SELECT'].includes(el.tagName);
  }

  function onKeydown(e: KeyboardEvent) {
    if (isTypingTarget(e.target)) return;

    const meta = e.ctrlKey || e.metaKey;
    if (meta && e.code === 'KeyZ') {
      e.preventDefault();
      if (e.shiftKey) editor.redo();
      else editor.undo();
      return;
    }
    if (meta && e.code === 'KeyY') {
      e.preventDefault();
      editor.redo();
      return;
    }
    if (meta && e.key.toLowerCase() === 's') {
      e.preventDefault();
      handleExport();
      return;
    }
    if ((e.key === 'Delete' || e.key === 'Backspace') && editor.selection.length > 0) {
      e.preventDefault();
      editor.deleteSelection();
      return;
    }
    if (e.key === 'Escape') {
      editor.clearSelection();
      editor.setTool('select');
      return;
    }
    const tool = HOTKEYS[e.key.toLowerCase()];
    if (tool && !meta) {
      e.preventDefault();
      editor.setTool(tool);
    }
  }

  onMount(() => {
    theme.init();
    const detachPaste = attachPasteHandler(handleFile);
    window.addEventListener('keydown', onKeydown);
    editor.restoreLast();
    return () => {
      detachPaste();
      window.removeEventListener('keydown', onKeydown);
    };
  });
</script>

<div class="flex h-full w-full">
  <!-- Слои — слева у границы -->
  <LayersPanel />

  <!-- Центральная область: Canvas + угловые панели + плавающая панель инструментов -->
  <main
    bind:this={canvasContainer}
    class="relative flex-1 min-w-0"
    ondrop={onDrop}
    ondragover={onDragOver}
    ondragleave={onDragLeave}
  >
    <Canvas>
      {#snippet empty()}
        <div class="absolute inset-0 flex items-center justify-center p-8">
          <div
            class="flex flex-col items-center gap-4 text-center max-w-md transition-colors rounded-2xl border-2 border-dashed p-12 {dragging ? 'border-accent bg-[rgba(255,69,58,0.05)]' : 'border-edge'}"
          >
            <div class="text-6xl">🖼️</div>
            <h1 class="text-xl font-semibold">Аннотатор скриншотов</h1>
            <p class="text-ink-dim text-sm">
              Вставьте скриншот <kbd class="px-1.5 py-0.5 rounded bg-panel-2 border border-edge text-xs">Ctrl</kbd>+<kbd class="px-1.5 py-0.5 rounded bg-panel-2 border border-edge text-xs">V</kbd>,
              перетащите файл сюда или
            </p>
            <button class="btn" onclick={() => fileInput?.click()}>
              Загрузить изображение
            </button>
            <p class="text-xs text-ink-dim">PNG · JPEG · WebP · GIF · BMP · AVIF</p>
          </div>
        </div>
      {/snippet}
    </Canvas>

    {#if editor.project.image}
      <!-- Левый нижний угол: undo / redo / zoom -->
      <div class="absolute bottom-4 left-4 z-20 flex items-center gap-1 panel rounded-xl px-1.5 py-1.5 shadow-lg">
        <button class="btn-icon" title="Отменить (Ctrl+Z)" disabled={!editor.canUndo} onclick={() => editor.undo()}>
          <Icon name="undo" size={18} />
        </button>
        <button class="btn-icon" title="Вернуть (Ctrl+Shift+Z)" disabled={!editor.canRedo} onclick={() => editor.redo()}>
          <Icon name="redo" size={18} />
        </button>
        <div class="w-px h-6 bg-edge mx-1"></div>
        <button class="btn-icon" title="Уменьшить" onclick={() => zoom(-0.1)} aria-label="Уменьшить">
          <Icon name="minus" size={18} />
        </button>
        <button
          class="text-sm font-semibold tabular-nums w-12 text-center text-ink-dim hover:text-ink cursor-pointer focus:outline-none transition-colors"
          title="Сбросить масштаб до 100%"
          onclick={() => editor.setViewport(1, editor.project.viewport.tx, editor.project.viewport.ty)}
        >
          {scalePct}%
        </button>
        <button class="btn-icon" title="Увеличить" onclick={() => zoom(0.1)} aria-label="Увеличить">
          <Icon name="plus" size={18} />
        </button>
      </div>


      <!-- Плавающая панель инструментов снизу по центру (как в Figma) -->
      <div class="absolute bottom-4 left-1/2 -translate-x-1/2 z-30">
        <Toolbar floating />
      </div>
    {/if}

    {#if dragging}
      <div class="absolute inset-0 border-4 border-accent pointer-events-none flex items-center justify-center bg-[rgba(255,69,58,0.1)]">
        <span class="text-accent text-2xl font-bold">Отпустите, чтобы открыть</span>
      </div>
    {/if}
  </main>

  <!-- Настройки выбранного инструмента — справа у границы -->
  <SettingsPanel />
</div>

<input
  bind:this={fileInput}
  type="file"
  accept="image/*"
  class="hidden"
  onchange={onFileInputChange}
/>
